import React from "react";

type SelectablePanelProps = {
  id: string;
  name: string;
  onClick: (event?: React.MouseEvent<HTMLInputElement | HTMLButtonElement, MouseEvent>) => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement | HTMLButtonElement>) => void;
  role: string;
  text: string;
  type: string;
  value: string;
  ariaLabel?: string;
  checked?: boolean;
  children?: React.ReactNode;
  className?: string;
  containerClass?: string;
  dataTestId?: string;
  defaultChecked?: boolean;
  disabled?: boolean;
  errorMessage?: string;
  hideChildrenText?: "true" | "false";
  location?: boolean;
  metaMessage?: string;
  mileageBenefits?: any;
  price?: number;
  pricingLabel?: string;
  serviceClass?: string;
};

export const SelectablePanel = ({
  ariaLabel = "",
  checked = false,
  children,
  className = "",
  containerClass = "",
  dataTestId,
  defaultChecked = false,
  disabled = false,
  errorMessage,
  hideChildrenText = "false",
  id,
  location = false,
  metaMessage,
  mileageBenefits,
  name,
  onClick,
  onKeyDown,
  price,
  pricingLabel,
  role,
  text,
  type,
  value,
  serviceClass = "",
}: SelectablePanelProps) => {
  // Format serviceText to ID for screen readers
  const serviceTextId = id.replace(/\s+/g, "-");
  const hasPrice = !!pricingLabel || !!price;

  // Only reference ids that are rendered so the accessible name is built from existing text.
  const labelledBy = [
    `${serviceTextId}-service`,
    mileageBenefits ? `${serviceTextId}-mileage` : hasPrice && `${serviceTextId}-price`,
    errorMessage && `${serviceTextId}-error`,
  ]
    .filter(Boolean)
    .join(" ");

  const isButton = type === "button";
  // Button panels take their accessible name from their own visible text, so it must not be hidden.
  const textAriaHidden = isButton ? undefined : "true";

  const renderBenefits = () => {
    if (mileageBenefits) {
      return (
        <div className="c-selectable-panel__mi" aria-hidden={textAriaHidden} id={`${serviceTextId}-mileage`}>
          {mileageBenefits}
        </div>
      );
    }

    return (
      <div className={`c-selectable-panel__pr ${className}`} aria-hidden={textAriaHidden} id={`${serviceTextId}-price`}>
        {pricingLabel}
        {!!price && ` $${price}`}
      </div>
    );
  };

  const content = (
    <div
      className={(location ? "flex-container selectable-location " : "flex-container ") + containerClass}
      aria-hidden={hideChildrenText}
    >
      <span
        id={`${serviceTextId}-service`}
        className={`c-selectable-panel__bd ${serviceClass} ${className}`}
        aria-hidden={textAriaHidden}
      >
        {text}
      </span>
      {children}
      <div className="flex-item-4">
        {renderBenefits()}
        {errorMessage && (
          <div className="c-selectable-panel__er" id={`${serviceTextId}-error`}>
            {errorMessage}
          </div>
        )}
      </div>
    </div>
  );

  const meta = metaMessage && <span className="c-selectable-panel__mt">{metaMessage}</span>;
  const panelClassName = `c-selectable-panel ${disabled ? `is-disabled` : ""} ${className}`;

  // Button panels render the visible text inside the <button> so the accessible name comes from real
  // on-screen text. This gives one screen reader stop per panel, and TalkBack doesn't need text recognition
  // (an empty button named via aria-label/aria-labelledby gets an extra "Detected: text …" OCR announcement).
  if (isButton) {
    return (
      <button
        onClick={onClick}
        onKeyDown={onKeyDown}
        role={role}
        type="button"
        id={id}
        name={name}
        className={`${panelClassName} c-selectable-panel--button`}
        data-testid={dataTestId}
        value={value}
        disabled={disabled}
      >
        {content}
        {meta}
      </button>
    );
  }

  return (
    <label htmlFor={id} className={panelClassName} data-testid={dataTestId}>
      <input
        onClick={onClick}
        onKeyDown={onKeyDown}
        role={role}
        type={type}
        id={id}
        name={name}
        aria-label={ariaLabel}
        className="c-selectable-panel__input"
        value={value}
        defaultChecked={defaultChecked || checked}
        disabled={disabled}
        aria-labelledby={labelledBy}
      />
      {content}
      {meta}
    </label>
  );
};
